import { Button, Dialog } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { IncomeSourceDto } from "../dto.js";

/**
 * "Remove this record" — the other half, and the destructive one.
 *
 * ==========================================================================================
 * IT OFFERS THE OTHER ACTION RATHER THAN JUST WARNING ABOUT ITSELF
 * ==========================================================================================
 *
 * A member reaching for this has almost certainly stopped receiving the income, which is the OTHER
 * control. So the dialog says what removing does — it disappears from every month, including years
 * already looked at — and then offers "It stopped instead" as a real button rather than telling them
 * to close this and find the other one.
 *
 * That follows `LeaveHouseholdDialog`'s rule: a refusal or a warning without the next step is a dead
 * end rather than a rule. Here it is not even a refusal — both actions are legitimate, and the whole
 * job of this dialog is making sure the right one is chosen.
 *
 * NO DATE FIELD, deliberately. Removing is not an event with a date; it is a statement that the
 * record should never have existed.
 */
export interface RemoveSourceDialogProps {
  open: boolean;
  onClose: () => void;
  source: IncomeSourceDto;
  onConfirm: () => void;
  /** Switches to the ending flow, which is what most people reaching for this actually want. */
  onEndInstead: () => void;
  pending: boolean;
  error?: string | null;
}

export function RemoveSourceDialog({
  open,
  onClose,
  source,
  onConfirm,
  onEndInstead,
  pending,
  error,
}: RemoveSourceDialogProps): ReactNode {
  const t = useT();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Remove ${source.name}?`}
      description={t("inc.remove.forNeverExisted")}
    >
      <div className="space-y-4" role="alert">
        <p>
          {t("inc.remove.willDisappear")}
          <strong>every month</strong>, including years you have already looked at. Your totals for
          those years will go down.
        </p>

        <div className="rounded-control border border-line bg-panel p-4">
          <p className="text-sm">
            {t("inc.remove.ifReal")}
            <strong>stopped</strong>, end it instead — that keeps the months you earned it and only
            stops it counting from then on.
          </p>
          <Button type="button" className="mt-3" onClick={onEndInstead} disabled={pending}>
            {t("inc.remove.stoppedInstead")}
          </Button>
        </div>

        {error ? (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="button" variant="primary" onClick={onConfirm} disabled={pending}>
            {pending ? t("common.removing") : t("inc.remove.heading")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
